import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { Group } from "three";

interface SceneBranchesProps {
  /** World position of the parent tree base. */
  center: [number, number, number];
  hue?: number;
}

/**
 * Wilds scene setup: two branches (title scene + end scene) that grow
 * out of the tree trunk. They extend outward over ~1.5s.
 */
export function SceneBranches({ center, hue = 110 }: SceneBranchesProps) {
  return (
    <group position={center}>
      <GrowingBranch
        side="left"
        angle={-0.5}
        height={2.0}
        hue={hue + 20}
      />
      <GrowingBranch
        side="right"
        angle={0.5}
        height={2.4}
        hue={hue - 15}
      />
    </group>
  );
}

function GrowingBranch({
  side,
  angle,
  height,
  hue,
}: {
  side: "left" | "right";
  angle: number;
  height: number;
  hue: number;
}) {
  const groupRef = useRef<Group>(null);
  const startTime = useRef<number | null>(null);

  const branchLen = 1.4;
  const xDir = side === "left" ? -1 : 1;
  const leafColor = `hsl(${((hue % 360) + 360) % 360}, 75%, 50%)`;

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const group = groupRef.current;
    if (!group) return;

    const delay = side === "right" ? 0.4 : 0;
    const localT = Math.max(0, t - delay);

    if (localT < 1.0) {
      const p = localT / 1.0;
      const eased = 1 - Math.pow(1 - p, 3);
      group.scale.set(eased, eased, eased);
    } else {
      group.scale.set(1, 1, 1);
    }
  });

  return (
    <group
      ref={groupRef}
      position={[0, height, 0]}
      rotation={[0, 0, angle * xDir]}
      scale={0}
    >
      {/* Branch stem */}
      <mesh position={[xDir * branchLen * 0.5, 0, 0]}>
        <cylinderGeometry args={[0.03, 0.05, branchLen, 6]} />
        <meshStandardMaterial color="#6b5a45" roughness={0.9} />
      </mesh>
      {/* Leaf cluster at tip */}
      <mesh position={[xDir * branchLen, 0.1, 0]}>
        <dodecahedronGeometry args={[0.35, 1]} />
        <meshStandardMaterial color={leafColor} roughness={0.7} />
      </mesh>
    </group>
  );
}
